import type { ReactNode } from 'react'
import type { ShowResultsReleasePayloadOutput } from '@schemas'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  categoryMix,
  entriesPerEntrant,
  honeyColours,
  percent,
  ribbonOdds,
  share,
  type ClassOdds,
  type CountRow,
} from './highlights'

// The exact figures behind each Highlights chart. The public page offers them as the table
// alternative to a chart and the staff review lists them, so both carry the same values.
type Results = ShowResultsReleasePayloadOutput

export const UNAVAILABLE = {
  odds: 'Ribbon odds are unavailable: no class had both entries and awards.',
  categories: 'No entries were recorded, so there is no category mix.',
  honey:
    'The light, amber and dark split is unavailable: this show has no classes identified as light, amber or dark extracted honey.',
  perEntrant: 'How many entries each entrant brought is unavailable for this release.',
}

/** A keyboard-focusable table whose first column heads each row. Shared by the public pages. */
export function DataTable({
  caption,
  headers,
  rows,
  visibleCaption = false,
}: {
  caption: string
  headers: string[]
  rows: ReactNode[][]
  visibleCaption?: boolean
}) {
  return (
    <Table tabIndex={0} className={visibleCaption ? 'caption-top' : undefined}>
      <caption className={visibleCaption ? 'pb-1 text-left font-medium' : 'sr-only'}>
        {caption}
      </caption>
      <TableHeader>
        <TableRow>
          {headers.map(heading => (
            <TableHead key={heading} scope="col">
              {heading}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={index}>
            {row.map((cell, column) =>
              column === 0 ? (
                <TableHead key={column} scope="row">
                  {cell}
                </TableHead>
              ) : (
                <TableCell key={column}>{cell}</TableCell>
              )
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

type TableProps = { visibleCaption?: boolean }

export function RibbonOddsTable({ classes, ...props }: { classes: ClassOdds[] } & TableProps) {
  return (
    <DataTable
      {...props}
      caption="Ribbon odds by class"
      headers={['Class', 'Division', 'Entries', 'Placed', 'Ribbon odds']}
      rows={classes.map(cls => [
        `${cls.code} · ${cls.name}`,
        cls.division,
        cls.entries.toLocaleString(),
        cls.placed.toLocaleString(),
        cls.unavailable ? `Unavailable: ${cls.unavailable.toLowerCase()}` : percent(cls.odds),
      ])}
    />
  )
}

export function CategoryMixTable({ rows, ...props }: { rows: CountRow[] } & TableProps) {
  const total = rows.reduce((sum, row) => sum + row.entries, 0)
  return (
    <DataTable
      {...props}
      caption="Entries by category"
      headers={['Category', 'Entries', 'Share of entries']}
      rows={rows.map(row => [
        row.label,
        row.entries.toLocaleString(),
        percent(share(row.entries, total)),
      ])}
    />
  )
}

export function HoneyColoursTable({
  rows,
  ...props
}: { rows: NonNullable<ReturnType<typeof honeyColours>> } & TableProps) {
  return (
    <DataTable
      {...props}
      caption="Extracted honey entries by colour class"
      headers={['Colour class', 'Entries']}
      rows={rows.map(row => [
        row.label,
        row.entries === null ? 'Unavailable: no class identified' : row.entries.toLocaleString(),
      ])}
    />
  )
}

export function EntriesPerEntrantTable({
  bins,
  ...props
}: { bins: Results['exhibitsPerEntrant'] } & TableProps) {
  return (
    <DataTable
      {...props}
      caption="Entrants by how many entries they brought"
      headers={['Entries per entrant', 'Entrants', 'Entries']}
      rows={bins.map(bin => [
        bin.label,
        bin.entrants.toLocaleString(),
        bin.entries.toLocaleString(),
      ])}
    />
  )
}

/** Every Highlights figure as a table, for the staff review of what will be released. */
export function HighlightsReview({ payload }: { payload: Results }) {
  const odds = ribbonOdds(payload)
  const categories = categoryMix(payload)
  const honey = honeyColours(payload)
  const perEntrant = entriesPerEntrant(payload)
  return (
    <div className="space-y-3">
      <p className="font-medium">Highlights page figures to release</p>
      {odds.some(cls => cls.odds !== null) ? (
        <div className="max-h-96 overflow-y-auto">
          <RibbonOddsTable classes={odds} visibleCaption />
        </div>
      ) : (
        <p>{UNAVAILABLE.odds}</p>
      )}
      {categories ? (
        <CategoryMixTable rows={categories} visibleCaption />
      ) : (
        <p>{UNAVAILABLE.categories}</p>
      )}
      {honey ? <HoneyColoursTable rows={honey} visibleCaption /> : <p>{UNAVAILABLE.honey}</p>}
      {perEntrant ? (
        <EntriesPerEntrantTable bins={perEntrant} visibleCaption />
      ) : (
        <p>{UNAVAILABLE.perEntrant}</p>
      )}
    </div>
  )
}
